import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Top-level error boundary. Catches any unhandled render error in the app
 * tree and renders a friendly fallback instead of a blank white page.
 *
 * Crucially, it also logs the full error + component stack to the console
 * so the bug can be diagnosed in production (and to our analytics ingest if
 * we add remote error reporting later).
 */
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error);
    console.error("[ErrorBoundary] componentStack:", info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-muted p-6">
          <div className="max-w-lg w-full bg-card border rounded-2xl shadow-lg p-6 space-y-4">
            <div>
              <p className="text-sm uppercase tracking-wider text-destructive font-bold">
                Erro inesperado
              </p>
              <h1 className="text-xl font-bold text-foreground mt-1">
                Algo deu errado ao carregar a página
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Recarregue a página. Se o erro persistir, nosso time já foi notificado.
              </p>
            </div>

            <details className="rounded-lg border bg-muted/40 p-3 text-xs">
              <summary className="cursor-pointer font-semibold text-muted-foreground">
                Detalhes técnicos
              </summary>
              <div className="mt-2 space-y-2">
                <p className="font-mono text-destructive break-all">
                  {this.state.error.name}: {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <pre className="overflow-auto max-h-40 text-[10px] text-muted-foreground whitespace-pre-wrap">
                    {this.state.error.stack.split("\n").slice(0, 8).join("\n")}
                  </pre>
                )}
              </div>
            </details>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  this.setState({ error: null, componentStack: null });
                }}
                className="flex-1 rounded-md border px-4 py-2 text-sm font-semibold hover:bg-accent"
              >
                Tentar novamente
              </button>
              <a
                href="/"
                className="flex-1 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold text-center hover:bg-primary/90"
              >
                Ir para o início
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
